const MUTE_KEY = 'candleClashMuted';
const MASTER_VOLUME = 0.6;
const MUSIC_VOLUME = 0.8;
const MAX_COMBO = 7;

const BPM = 132;
const STEP = 60 / BPM / 4;
const LOOKAHEAD = 0.15;
const CHORDS = [
  { bass: 45, triad: [57, 60, 64] },
  { bass: 41, triad: [53, 57, 60] },
  { bass: 48, triad: [60, 64, 67] },
  { bass: 43, triad: [55, 59, 62] },
];
const BASS_PATTERN = [0, 0, 12, 0, 0, 12, 0, 7];

const hz = (midi) => 440 * 2 ** ((midi - 69) / 12);

function createSound() {
  let ctx = null;
  let master = null;
  let musicBus = null;
  let reverbIn = null;
  let noiseBuffer = null;
  let lastFlapAt = 0;
  let combo = 0;
  let musicTimer = 0;
  let nextStepAt = 0;
  let step = 0;
  let muted = false;
  const pulses = {};
  try {
    muted = localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    muted = false;
  }

  const buildReverb = () => {
    const length = Math.floor(ctx.sampleRate * 1.3);
    const impulse = ctx.createBuffer(2, length, ctx.sampleRate);
    for (let c = 0; c < 2; c += 1) {
      const data = impulse.getChannelData(c);
      for (let i = 0; i < length; i += 1) data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 2.6;
    }
    const convolver = ctx.createConvolver();
    const wet = ctx.createGain();
    reverbIn = ctx.createGain();
    wet.gain.value = 0.45;
    convolver.buffer = impulse;
    reverbIn.connect(convolver);
    convolver.connect(wet);
    wet.connect(master);
  };

  const unlock = () => {
    if (ctx) {
      if (ctx.state === 'suspended') ctx.resume();
      return;
    }
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    ctx = new AudioContextClass();
    master = ctx.createGain();
    master.gain.value = muted ? 0 : MASTER_VOLUME;
    const compressor = ctx.createDynamicsCompressor();
    master.connect(compressor);
    compressor.connect(ctx.destination);
    musicBus = ctx.createGain();
    musicBus.gain.value = 0;
    musicBus.connect(master);
    buildReverb();
  };

  const pulseWave = (duty) => {
    if (!pulses[duty]) {
      const harmonics = 48;
      const real = new Float32Array(harmonics);
      const imag = new Float32Array(harmonics);
      for (let n = 1; n < harmonics; n += 1) {
        real[n] = Math.sin(2 * Math.PI * n * duty) / (Math.PI * n);
        imag[n] = (1 - Math.cos(2 * Math.PI * n * duty)) / (Math.PI * n);
      }
      pulses[duty] = ctx.createPeriodicWave(real, imag);
    }
    return pulses[duty];
  };

  const envelope = (gain, t, vol, dur, attack = 0.006) => {
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.linearRampToValueAtTime(vol, t + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  };

  const voice = ({ type, duty = 0.5, from, to = from, dur = 0.1, vol = 0.1, delay = 0, cutoff = 5000, wet = 0, dest, attack }) => {
    if (!ctx || muted) return;
    const t = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    if (type) osc.type = type;
    else osc.setPeriodicWave(pulseWave(duty));
    osc.frequency.setValueAtTime(from, t);
    if (to !== from) osc.frequency.exponentialRampToValueAtTime(to, t + dur);
    filter.type = 'lowpass';
    filter.frequency.value = cutoff;
    envelope(gain, t, vol, dur, attack);
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(dest || master);
    if (wet > 0) {
      const send = ctx.createGain();
      send.gain.value = wet;
      gain.connect(send);
      send.connect(reverbIn);
    }
    osc.start(t);
    osc.stop(t + dur + 0.05);
  };

  const noise = ({ dur = 0.3, vol = 0.3, from = 2600, to = 180, filterType = 'lowpass', delay = 0, dest }) => {
    if (!ctx || muted) return;
    if (!noiseBuffer) {
      noiseBuffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const data = noiseBuffer.getChannelData(0);
      for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
    }
    const t = ctx.currentTime + delay;
    const source = ctx.createBufferSource();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    source.buffer = noiseBuffer;
    filter.type = filterType;
    filter.frequency.setValueAtTime(from, t);
    if (to !== from) filter.frequency.exponentialRampToValueAtTime(to, t + dur);
    envelope(gain, t, vol, dur, 0.003);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(dest || master);
    source.start(t);
    source.stop(t + dur + 0.02);
  };

  const playStep = (n, when) => {
    const chord = CHORDS[(n >> 4) & 3];
    const s = n & 15;
    const delay = when - ctx.currentTime;
    if (s % 2 === 0) {
      voice({ type: 'triangle', from: hz(chord.bass + BASS_PATTERN[s / 2]), dur: STEP * 1.8, vol: 0.14, delay, dest: musicBus, cutoff: 900 });
    }
    const note = chord.triad[s % 3] + 12 + (s % 8 >= 4 ? 12 : 0);
    voice({ duty: 0.125, from: hz(note), dur: STEP * 0.9, vol: 0.03, delay, dest: musicBus, cutoff: 3200, wet: 0.2 });
    if (s % 4 === 2) noise({ dur: 0.04, vol: 0.05, from: 7000, filterType: 'highpass', delay, dest: musicBus });
  };

  const schedule = () => {
    while (nextStepAt < ctx.currentTime + LOOKAHEAD) {
      playStep(step, nextStepAt);
      nextStepAt += STEP;
      step = (step + 1) % 64;
    }
  };

  const musicStart = (restart) => {
    if (!ctx || musicTimer) return;
    if (restart) step = 0;
    nextStepAt = ctx.currentTime + 0.05;
    musicBus.gain.setTargetAtTime(MUSIC_VOLUME, ctx.currentTime, 0.05);
    musicTimer = window.setInterval(schedule, 25);
  };

  const musicStop = () => {
    window.clearInterval(musicTimer);
    musicTimer = 0;
    if (musicBus) musicBus.gain.setTargetAtTime(0, ctx.currentTime, 0.06);
  };

  return {
    unlock,
    isMuted: () => muted,
    setMuted(next) {
      muted = next;
      try {
        localStorage.setItem(MUTE_KEY, next ? '1' : '0');
      } catch {
        /* storage unavailable: the choice just won't persist */
      }
      if (master) master.gain.setTargetAtTime(next ? 0 : MASTER_VOLUME, ctx.currentTime, 0.02);
    },
    musicStart() {
      combo = 0;
      musicStart(true);
    },
    musicPause: musicStop,
    musicResume: () => musicStart(false),
    musicStop,
    flap() {
      unlock();
      const now = performance.now();
      if (now - lastFlapAt < 60) return;
      lastFlapAt = now;
      const r = 0.97 + Math.random() * 0.06;
      voice({ duty: 0.25, from: 380 * r, to: 700 * r, dur: 0.08, vol: 0.06, cutoff: 2600 });
      voice({ type: 'triangle', from: 190 * r, to: 350 * r, dur: 0.09, vol: 0.09 });
    },
    pass() {
      voice({ type: 'triangle', from: 660, to: 880, dur: 0.08, vol: 0.08 });
      voice({ duty: 0.25, from: 1320, dur: 0.06, vol: 0.025, delay: 0.02 });
    },
    coin() {
      const lift = 2 ** (Math.min(combo, MAX_COMBO) / 12);
      combo += 1;
      voice({ from: 988 * lift, dur: 0.07, vol: 0.07, wet: 0.15 });
      voice({ from: 1319 * lift, dur: 0.26, vol: 0.07, delay: 0.07, wet: 0.25 });
      voice({ type: 'triangle', from: 1319 * lift * 1.005, dur: 0.22, vol: 0.05, delay: 0.07 });
    },
    hit() {
      combo = 0;
      musicStop();
      noise({ dur: 0.35, vol: 0.35, from: 3200, to: 120 });
      voice({ type: 'sine', from: 110, to: 35, dur: 0.45, vol: 0.3 });
      voice({ type: 'sawtooth', from: 220, to: 55, dur: 0.4, vol: 0.14, cutoff: 900 });
      [392, 330, 262].forEach((from, i) => voice({ from, dur: 0.14, vol: 0.05, delay: 0.32 + i * 0.15, wet: 0.3 }));
    },
    record() {
      [523, 659, 784, 1047].forEach((from, i) => voice({ duty: 0.25, from, dur: 0.14, vol: 0.07, delay: i * 0.07, wet: 0.3 }));
    },
    reward() {
      const base = 0.3;
      [1047, 1319, 1568, 2093].forEach((from, i) =>
        voice({ type: 'triangle', from, dur: 0.22, vol: 0.12, delay: base + i * 0.08, wet: 0.4 })
      );
      voice({ type: 'sine', from: 523, dur: 0.8, vol: 0.1, delay: base, wet: 0.3 });
      voice({ type: 'sine', from: 784, dur: 0.8, vol: 0.08, delay: base, wet: 0.3 });
      noise({ dur: 0.5, vol: 0.04, from: 6000, filterType: 'highpass', delay: base });
    },
    ui() {
      unlock();
      voice({ from: 880, to: 1100, dur: 0.05, vol: 0.05 });
    },
    success() {
      voice({ from: 784, dur: 0.1, vol: 0.07, wet: 0.2 });
      voice({ from: 1047, dur: 0.25, vol: 0.07, delay: 0.1, wet: 0.3 });
    },
  };
}

export const sound = createSound();
